import { toggleMockExamStatusApi } from "@/api/services/admin/learning-content";
import { TMockExamListItemDto } from "@/api/services/admin/learning-content/dtos/get-mock-exam.dto";
import { Button } from "@/components/elements/Button";
import { Table } from "@/components/elements/Table";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { EMPTY_STR } from "@/constants/string";
import { useTable } from "@/hooks/useTable";
import { useToast } from "@/hooks/useToast";
import { AppRoutes } from "@/routes/config";
import { background, black, button } from "@/theme/colors";
import { typography } from "@/theme/typography";
import { showError } from "@/utils/error";
import { getDynamicRoute } from "@/utils/url";
import { Box, Switch as MuiSwitch, Typography, styled } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { MRT_ColumnDef, MRT_Row } from "material-react-table";
import { FC, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

type TMockExamsTableProps = {
  isLoading: boolean;
  isFetching?: boolean;
  data: TMockExamListItemDto[];
  industryId: string | number;
  onDragEnd: (draggedRow: MRT_Row<TMockExamListItemDto>, targetRow: MRT_Row<TMockExamListItemDto> | null) => void;
};

// Mock exam list table with drag-to-reorder + 有効 toggle (mirrors WEEK's SubjectsTable)
export const MockExamsTable: FC<TMockExamsTableProps> = ({ isLoading, isFetching, data, industryId, onDragEnd }) => {
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [togglingIds, setTogglingIds] = useState<Set<number>>(new Set());

  const handleToggleStatus = async (id: number, currentIsActive: boolean) => {
    setTogglingIds((prev) => new Set(prev).add(id));
    try {
      await toggleMockExamStatusApi(String(industryId), String(id), { isActive: !currentIsActive });
      toast.success(API_SUCCESS_MESSAGE);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.LEARNING_CONTENT.FETCH_MOCK_EXAM_LIST, String(industryId)] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.LEARNING_CONTENT.FETCH_DETAIL, String(industryId)] });
    } catch (error) {
      showError(error);
    } finally {
      setTogglingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const handleEdit = (mockExamId: number) => {
    navigate(getDynamicRoute(AppRoutes.learningContentMockExamEdit, { id: String(industryId), mockExamId: String(mockExamId) }));
  };

  const columns = useMemo<MRT_ColumnDef<TMockExamListItemDto>[]>(
    () => [
      {
        header: "有効",
        accessorKey: "isActive",
        size: 72,
        muiTableHeadCellProps: { sx: { width: "72px" } },
        muiTableBodyCellProps: { sx: { width: "72px" } },
        Cell: ({ row }) => {
          const { id, isActive, totalQuestionCount } = row.original;
          const hasNoQuestions = (totalQuestionCount ?? 0) === 0;
          return (
            <IOSSwitch
              checked={isActive}
              disabled={togglingIds.has(id) || hasNoQuestions}
              onChange={() => handleToggleStatus(id, isActive)}
              inputProps={{ "aria-label": "有効" }}
            />
          );
        },
        enableSorting: false,
      },
      {
        header: "模擬試験名",
        accessorKey: "name",
        grow: true,
        muiTableHeadCellProps: { sx: { flex: 1 } },
        muiTableBodyCellProps: { sx: { flex: 1 } },
        Cell: ({ row }) => {
          const name = row.original.name?.trim() ? row.original.name : EMPTY_STR.TEXT;
          const detailUrl = getDynamicRoute(AppRoutes.learningContentMockExamDetail, {
            id: String(industryId),
            mockExamId: String(row.original.id),
          });
          return <Link to={detailUrl}>{name}</Link>;
        },
      },
      {
        header: "科目数",
        accessorKey: "totalSubjectCount",
        size: 74,
        muiTableHeadCellProps: { sx: { width: "74px" } },
        muiTableBodyCellProps: { sx: { width: "74px" } },
        Cell: ({ row }) => row.original.totalSubjectCount ?? 0,
        enableSorting: false,
      },
      {
        header: "問題数",
        accessorKey: "totalQuestionCount",
        size: 74,
        muiTableHeadCellProps: { sx: { width: "74px" } },
        muiTableBodyCellProps: { sx: { width: "74px" } },
        Cell: ({ row }) => {
          const count = row.original.totalQuestionCount ?? 0;
          return count === 0 ? <Typography color={button.danger}>{count}</Typography> : count;
        },
        enableSorting: false,
      },
      {
        header: "合格点/満点",
        id: "passingTotal",
        size: 110,
        muiTableHeadCellProps: { sx: { width: "110px" } },
        muiTableBodyCellProps: { sx: { width: "110px" } },
        Cell: ({ row }) => {
          const { passingScore, totalScore } = row.original;
          return `${passingScore ?? EMPTY_STR.TEXT} / ${totalScore ?? EMPTY_STR.TEXT}`;
        },
        enableSorting: false,
      },
      {
        header: "配点合計",
        accessorKey: "totalScoreSum",
        size: 84,
        muiTableHeadCellProps: { sx: { width: "84px" } },
        muiTableBodyCellProps: { sx: { width: "84px" } },
        Cell: ({ row }) => row.original.totalScoreSum ?? 0,
        enableSorting: false,
      },
      {
        header: "最終更新日",
        accessorKey: "lastUpdatedAt",
        size: 106,
        muiTableHeadCellProps: { sx: { width: "106px" } },
        muiTableBodyCellProps: { sx: { width: "106px" } },
        Cell: ({ row }) => row.original.lastUpdatedAt || EMPTY_STR.TEXT,
        enableSorting: false,
      },
      {
        header: "",
        id: "actions",
        size: 84,
        muiTableHeadCellProps: { sx: { width: "84px" } },
        muiTableBodyCellProps: { sx: { width: "84px" } },
        Cell: ({ row }) => (
          <Button size="sm" variant="outline" onClick={() => handleEdit(row.original.id)}>
            編集
          </Button>
        ),
        enableSorting: false,
      },
    ],
    [togglingIds, industryId],
  );

  const { table } = useTable({
    columns,
    rows: data ?? [],
    enableSorting: false,
    enableRowActions: false,
    enableRowOrdering: true,
    displayColumnDefOptions: {
      "mrt-row-drag": {
        header: "",
        size: 43,
      },
    },
    muiRowDragHandleProps: ({ table: tableInstance }) => ({
      onDragEnd: () => {
        const { draggingRow, hoveredRow } = tableInstance.getState();
        if (draggingRow && hoveredRow) {
          onDragEnd(draggingRow, hoveredRow as MRT_Row<TMockExamListItemDto>);
        }
      },
    }),
  });

  return (
    <TableWrapper>
      <Table table={table} isLoading={isLoading} isFetching={isFetching} />
    </TableWrapper>
  );
};

const TableWrapper = styled(Box)({
  "& .MuiTableCell-head": {
    fontSize: `${typography.body14.fontSize} !important`,
  },
});

const IOSSwitch = styled(MuiSwitch)(({ theme }) => ({
  width: 47,
  height: 26,
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: 2,
    transitionDuration: "300ms",
    color: black[90],
    "&.Mui-checked": {
      transform: "translateX(16px)",
      color: background.white,
      marginLeft: 7,
      "& + .MuiSwitch-track": {
        backgroundColor: button.primary,
        opacity: 1,
        border: 0,
      },
    },
    "&.Mui-focusVisible .MuiSwitch-thumb": {
      color: button.primary,
      border: `6px solid ${background.white}`,
    },
    "&.Mui-disabled": {
      color: black[95],
      cursor: "not-allowed",
      "& .MuiSwitch-thumb": {
        color: black[95],
      },
      "& + .MuiSwitch-track": {
        backgroundColor: black[99],
        border: 0,
        opacity: 1,
      },
    },
  },
  "& .MuiSwitch-thumb": {
    boxSizing: "border-box",
    width: 22,
    height: 22,
  },
  "& .MuiSwitch-track": {
    borderRadius: 26 / 2,
    border: `1px solid ${black[90]}`,
    backgroundColor: theme.palette.mode === "light" ? background.white : background.dark,
    opacity: 1,
    transition: theme.transitions.create(["background-color"], {
      duration: 500,
    }),
  },
}));
